// src/interfaces/chatTypes.ts
import { ChatApiError } from "../api/chatApi";
import { AutoRedlineOutput } from "./autoRedlineTypes";
import { AddCommentOutput } from "./addCommentTypes";
import { DocumentCitation } from "./documentCitationTypes";
import { WebCitationInsertion } from "./webCitationTypes";
import { FormRequestPayload } from "./formTypes";
import { NormalizedPlaybookResult, PlaybookSelectionResult } from "./playbookTypes";
import { NormalizedCatylexSearchResult, CatylexToolInput } from "./catylexSearchTypes";
import { NormalizedSharePointResult, SharePointToolInput } from "./sharepointSearchTypes";
import { OutlookMailResult, OutlookMailToolInput } from "./outlookMailTypes";

/**
 * Content structure for chat messages
 * Supports both text and thinking content types for comprehensive message representation
 */
export interface ChatMessageContent {
  text: string;
  thinking?: string; // AI reasoning process (only for assistant messages)
}

/**
 * Message structure for chat history
 * This format aligns with the FastAPI chat endpoint expectations.
 * Content is either a ChatMessageContent (text messages) or an array of
 * Anthropic content blocks (messages with tool_use/tool_result).
 */
export interface ChatMessage {
  role: "user" | "assistant";
  content: ChatMessageContent | unknown[];
}

/**
 * Enhanced configuration parameters for chat requests
 * Updated to support the new web search and thinking capabilities
 */
export interface ChatDetails {
  // New API-specific flags
  web_search?: boolean; // Enable web search capabilities
  web_fetch?: boolean; // Enable web fetch capabilities (auto-enabled with web_search)
  thinking?: boolean; // Include AI reasoning process in response

  // Legacy fields maintained for backward compatibility
  provider?: string;
  temperature?: number;
  focus_sections?: string[];

  // Selected playbook id for the session — sent on every request so the backend
  // fetches the chosen playbook (Catylex multi-playbook tenants).
  selected_playbook_id?: string;

  // Allow for future expansion
  [key: string]: any;
}

/**
 * Complete request parameters for chat endpoint
 * This follows the same pattern as your other API request interfaces
 */
export interface ChatRequest {
  message: string;
  full_file: File; // XML file with redlines (required)
  additional_files?: File[]; // Additional files for context (DOCX, XML, XLSX/XLS)
  selected_text_file?: File; // Optional XML file with selected text
  id?: string; // Session identifier for server-side history
  doc_id?: string; // Document identifier for caching
  bypass_cache?: boolean; // Force reprocessing flag
  details?: ChatDetails; // Enhanced configuration with new capabilities
  chat_history?: ChatMessage[]; // Previous conversation messages
  access_token?: string; // User access token
}

/**
 * Enhanced streaming response chunk from chat API
 * Updated to support all response types from the new API schema
 */
export interface ChatResponseChunk {
  text: string | object; // Now supports both string content and structured objects
  type: ChatResponseChunkType;
  error_code?: string; // Backend error code (e.g. "TOKEN_LIMIT"), present on error chunks
  stop_reason?: string; // LLM stop reason (e.g., "end_turn", "max_tokens"), present on message_stop chunks
  ai_trace_id?: string; // PostHog LLM-analytics trace for this turn, present on message_start chunks
}

/**
 * Comprehensive enumeration of all possible chunk types
 * This covers the full range of responses from the enhanced API
 */
export type ChatResponseChunkType =
  // Core message flow control
  | "message_start"
  | "message_stop"

  // Text content streaming
  | "text_start"
  | "text"
  | "text_stop"

  // AI reasoning process (thinking mode)
  | "thinking_start"
  | "thinking"
  | "thinking_stop"

  // Web search functionality
  | "server_tool_use"
  | "input_json_delta"
  | "web_search_tool_result"

  // Web fetch functionality
  | "web_fetch_tool_result"

  // Auto-redline functionality
  | "tool_use_start" // Tool execution started
  | "tool_use_result" // Tool execution completed with results

  // Workflow forms functionality
  | "form_request" // Workflow form request for user input

  // Document citations functionality
  | "document_citation" // Citation from uploaded document

  // Web citations rebuilt from Gemini's search grounding
  | "web_citations" // Where to splice <cite/> tags into the finished answer

  // Error handling
  | "error";

/**
 * Structured data types for web search functionality
 * These interfaces provide type safety for the new search features
 */
export interface WebSearchQuery {
  query: string;
}

export interface WebSearchResult {
  title: string;
  url: string;
  page_age?: string | null;
}

/**
 * Web fetch URL structure from input_json_delta
 * Note: This is informational only - the actual URL comes in the result
 */
export interface WebFetchUrl {
  url: string;
}

export interface ServerToolUse {
  name: string; // e.g., "web_search"
}

/**
 * Web fetch result structure from chat API
 * Contains either success data or error information
 */
export interface WebFetchResult {
  /** Status of the fetch operation */
  status: "success" | "error";

  /** The URL that was fetched */
  url: string;

  /** Page or document title (only for success) */
  title?: string;

  /** ISO timestamp when content was retrieved (only for success) */
  retrieved_at?: string;

  /** Error code if fetch failed (only for error) */
  error_code?: string;

  /** User-friendly error message if fetch failed (only for error) */
  error_message?: string;
}

/**
 * Type guards for structured text content
 * These functions provide type-safe access to the different object types
 * that can appear in the text field of response chunks
 */
export function isWebSearchQuery(obj: any): obj is WebSearchQuery {
  return obj && typeof obj === "object" && typeof obj.query === "string";
}

export function isWebFetchUrl(obj: any): obj is WebFetchUrl {
  return obj && typeof obj === "object" && typeof obj.url === "string";
}

export function isWebSearchResults(obj: any): obj is WebSearchResult[] {
  return (
    Array.isArray(obj) &&
    obj.every(
      (item) =>
        item &&
        typeof item.title === "string" &&
        typeof item.url === "string" &&
        (item.page_age === null || item.page_age === undefined || typeof item.page_age === "string")
    )
  );
}

export function isServerToolUse(obj: any): obj is ServerToolUse {
  return obj && typeof obj === "object" && typeof obj.name === "string";
}

export function isWebFetchResult(obj: any): obj is WebFetchResult {
  if (!obj || typeof obj !== "object" || typeof obj.status !== "string") {
    return false;
  }

  if (obj.status === "success") {
    // Success requires url
    return typeof obj.url === "string";
  } else if (obj.status === "error") {
    // Error requires error_code and error_message (url is optional)
    return typeof obj.error_code === "string" && typeof obj.error_message === "string";
  }

  return false;
}

/**
 * Error response structure from chat API
 * This follows the same pattern as your other API error interfaces
 */
export interface ChatError {
  status_code: number;
  error_code: string;
  message: string;
  request_id: string;
  timestamp: string;
  details?: any;
}

/**
 * Enhanced callbacks for streaming chat responses
 * Updated to handle the new response types while maintaining backward compatibility
 */
export interface ChatStreamCallbacks {
  onStart: () => void;

  // Core content callbacks
  onTextChunk: (chunk: string) => void;
  onThinkingChunk: (chunk: string) => void;

  // Enhanced flow control callbacks (optional for backward compatibility)
  /**
   * Fires on every message_start, so once per agentic iteration rather than
   * once per response. `aiTraceId` identifies this whole turn in PostHog LLM
   * analytics and is the same on every iteration; it is absent when the
   * backend predates the field or has analytics switched off.
   */
  onMessageStart?: (aiTraceId?: string) => void;
  onMessageStop?: (stopReason?: string) => void;
  onTextStart?: () => void;
  onTextStop?: () => void;
  onThinkingStart?: () => void;
  onThinkingStop?: (signature: string | null) => void;

  // Web search callbacks (optional, will be implemented when UI is ready)
  onWebSearchToolUse?: (toolInfo: ServerToolUse) => void;
  onWebSearchQuery?: (query: WebSearchQuery) => void;
  onWebSearchResults?: (results: WebSearchResult[]) => void;

  // Web fetch callbacks (optional)
  onWebFetchResult?: (result: WebFetchResult) => void;

  // Auto-redline callbacks (optional)
  onAutoRedlineToolResult?: (result: AutoRedlineOutput) => void;

  // Add-comment callbacks (optional)
  onAddCommentToolResult?: (result: AddCommentOutput) => void;

  // Workflow form callbacks (optional)
  onFormRequest?: (payload: FormRequestPayload) => void;

  // Playbook tool callbacks (optional)
  onPlaybookToolResult?: (result: NormalizedPlaybookResult) => void;

  // Playbook selection card callback (optional) — fires when the backend returns
  // an in-chat playbook picker instead of resolved content
  onPlaybookSelectionRequest?: (result: PlaybookSelectionResult) => void;

  // Catylex search callbacks (optional)
  onCatylexSearchResult?: (result: NormalizedCatylexSearchResult) => void;
  onCatylexToolInput?: (input: CatylexToolInput) => void;

  // SharePoint search callbacks (optional)
  onSharePointSearchResult?: (result: NormalizedSharePointResult) => void;
  onSharePointToolInput?: (input: SharePointToolInput) => void;

  // Outlook mail callbacks (optional)
  onOutlookMailResult?: (result: OutlookMailResult) => void;
  onOutlookMailToolInput?: (input: OutlookMailToolInput) => void;

  // Document citation callback (optional)
  onDocumentCitation?: (citation: DocumentCitation) => void;

  // Web citation insertions for the finished answer (optional, Gemini only)
  onWebCitations?: (insertions: WebCitationInsertion[]) => void;

  // Tool use notification (optional) — receives the tool name directly (e.g., "catylex_search", "auto_redline")
  onToolUseStart?: (toolName: string) => void;

  // Error and completion
  onError: (error: ChatApiError) => void;
  onComplete: () => void;
}

// Re-export types for use in other modules
export { ChatApiError } from "../api/chatApi";
export type { AutoRedlineOutput } from "./autoRedlineTypes";
export type { AddCommentOutput, CommentApplyStatus } from "./addCommentTypes";
export { isAddCommentOutput } from "./addCommentTypes";
export type { FormRequestPayload } from "./formTypes";
export { isFormRequestPayload } from "./formTypes";
export type {
  PlaybookToolResult,
  NormalizedPlaybookResult,
  PlaybookSelectionOption,
  PlaybookSelectionContent,
  PlaybookSelectionResult,
} from "./playbookTypes";
export {
  isPlaybookToolResult,
  isPlaybookSuccess,
  isPlaybookError,
  normalizePlaybookResult,
  isPlaybookSelectionResult,
  normalizePlaybookSelectionResult,
} from "./playbookTypes";
export type { NormalizedCatylexSearchResult, CatylexToolInput } from "./catylexSearchTypes";
export {
  isCatylexSearchResult,
  normalizeCatylexSearchResult,
  parseCatylexToolInput,
} from "./catylexSearchTypes";
export type { NormalizedSharePointResult, SharePointToolInput } from "./sharepointSearchTypes";
export {
  isSharePointSearchResult,
  normalizeSharePointResult,
  parseSharePointToolInput,
} from "./sharepointSearchTypes";
export type { OutlookMailResult, OutlookMailToolInput } from "./outlookMailTypes";
export { isOutlookMailResult, normalizeOutlookMailResult } from "./outlookMailTypes";
